import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCategoryClick = (url: string) => {
    window.open(url, "_blank", "noopener,nofollow");
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 group whitespace-nowrap"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <i className="ri-shopping-bag-3-line text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 tracking-tight">
              Kakobuy<span className="text-primary-600">Finds</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Kategorie</span>
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto animate-fade-in">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => {
                        handleCategoryClick(cat.url);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors text-left whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
            >
              kakobuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={isMobileMenuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden pb-6 animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="mb-5">
              <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-1 mb-3">
                Kategorie produktów
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => {
                      handleCategoryClick(cat.url);
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-3 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300/60 hover:bg-background-50 transition-all duration-200 text-left cursor-pointer group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-primary-600 text-sm`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-900 transition-colors leading-tight">
                      {cat.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5 pt-4 border-t border-background-200">
              <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-1 mb-3">
                Poradniki i artykuły
              </div>
              <div className="space-y-1">
                <Link
                  to="/artykul/kakobuy-spreadsheet-poradnik"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <div className="w-7 h-7 rounded-md bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-book-open-line text-secondary-600 text-xs"></i>
                  </div>
                  <span className="text-xs">Kompletny poradnik 2026</span>
                </Link>
                <Link
                  to="/artykul/kakobuy-vs-cssbuy"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <div className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-scales-3-line text-accent-600 text-xs"></i>
                  </div>
                  <span className="text-xs">Kakobuy vs CSSBuy</span>
                </Link>
                <Link
                  to="/artykul/bezpieczenstwo-zakupow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <div className="w-7 h-7 rounded-md bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-secondary-600 text-xs"></i>
                  </div>
                  <span className="text-xs">Bezpieczeństwo zakupów</span>
                </Link>
                <Link
                  to="/artykul/najlepsze-finds-2026"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <div className="w-7 h-7 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-sparkling-line text-primary-600 text-xs"></i>
                  </div>
                  <span className="text-xs">Najlepsze finds 2026</span>
                </Link>
                <Link
                  to="/artykul/superbuy-spreadsheet-poradnik"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <div className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-compass-3-line text-accent-600 text-xs"></i>
                  </div>
                  <span className="text-xs">Superbuy poradnik</span>
                </Link>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-medium text-center whitespace-nowrap"
              >
                kakobuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}